import React, { useState, useRef, useEffect } from 'react';
import { Modal, TitleBar } from '@shopify/app-bridge-react';
import {
  Layout,
  Button,
  ButtonGroup,
  Text,
  Box,
  Banner,
  TextField,
  Form,
  FormLayout,
  Spinner,
  InlineStack,
  BlockStack,
} from '@shopify/polaris';
import { StudioVideo } from '../ai-video-creator/stores/studioVideoStore';
import { useGetVideoUrl } from '@/hooks/useGetVideoUrl';
import {
  createSocialMediaSingleItem,
  publishSocialMediaContainer,
  getContainerStatus,
  getLimitUsage,
  MediaType,
  SocialMediaSources,
  ContainerStatus,
  InstagramAppScopes,
} from '@/api/social-media';
import { useCurrentApp } from '@/stores/userDataStore';
import { IntegrationService } from '@/features/integrations/services/IntegrationService';
import { useInstagramApps } from '@/features/integrations/hooks/useSocialMediaApps';
import LoadingBar from '@/components/LoadingBar';
import VideoCard from '@/components/videos/VideoCard';
import { assetType } from '@/types/entities';
import DismissibleBanner from '@/components/DismissibleBanner';
import { SessionBanner } from '@/types/banners';
type PublishToInstagramModalProps = {
  video: StudioVideo;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

export default function PublishToInstagramModal({ video, isOpen, setIsOpen }: PublishToInstagramModalProps) {
  const { appKey } = useCurrentApp();
  const assetUrl = useGetVideoUrl(video);
  const isVideo = !video.vodAsset?.type || video.vodAsset.type === assetType.video;
  const duration = Math.max(video.vodAsset?.duration || 0, 15);

  const [isPublishing, setIsPublishing] = useState(false);
  const [caption, setCaption] = useState('');
  const [error, setError] = useState<{
    title: string;
    body: string;
    action?: { content: string; onAction: () => void };
  } | null>(null);
  const [success, setSuccess] = useState(false);
  const [openDiscardDialog, setOpenDiscardDialog] = useState(false);

  const containerStatusController = new AbortController();
  const containerStatusSignal = containerStatusController.signal;

  const publishMediaContainerController = new AbortController();
  const publishMediaContainerSignal = publishMediaContainerController.signal;

  const getLimitUsageController = new AbortController();
  const getLimitUsageSignal = getLimitUsageController.signal;

  const createSingleItemController = new AbortController();
  const createSingleItemSignal = createSingleItemController.signal;
  const tryPublishTimeout = useRef<NodeJS.Timeout | null>(null);

  const { instagramApps, isAppsLoading } = useInstagramApps();

  useEffect(() => {
    if (isAppsLoading) {
      return;
    }
    if (!instagramApps.length) {
      setError({
        title: 'No Instagram Apps Connected',
        body: 'Please connect an Instagram app to publish to Instagram.',
        action: { content: 'Reconnect', onAction: handleReconnectInstagramApp },
      });
      return;
    }
    const instagramApp = instagramApps[0];
    const {
      data: { scopes },
    } = instagramApp;

    if (!scopes?.includes(InstagramAppScopes.INSTAGRAM_CONTENT_PUBLISH)) {
      setError({
        title: "You don't have permission to publish to Instagram",
        body: 'Please reconnect your Instagram app to publish to Instagram.',
        action: { content: 'Reconnect', onAction: handleReconnectInstagramApp },
      });
    }
  }, [instagramApps, isAppsLoading]);

  const handleReconnectInstagramApp = () => {
    IntegrationService.handleInstagramConnect({ appKey });
  };

  const errorTitle = 'Publish Failed';
  const errorMessage = 'Error publishing to Instagram. Please try again later.';

  const handleClose = () => {
    if (isPublishing) {
      setOpenDiscardDialog(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleDiscardPublish = () => {
    setOpenDiscardDialog(false);
    setIsPublishing(false);
    containerStatusController.abort();
    publishMediaContainerController.abort();
    getLimitUsageController.abort();
    createSingleItemController.abort();
    if (tryPublishTimeout.current) {
      clearTimeout(tryPublishTimeout.current);
    }
  };

  const tryPublish = async (creationId, attempt = 0) => {
    try {
      const containerStatus = await getContainerStatus(
        { appKey, containerId: creationId, source: SocialMediaSources.instagram },
        containerStatusSignal
      );
      if (
        containerStatus.status_code === ContainerStatus.ERROR ||
        containerStatus.status_code === ContainerStatus.EXPIRED ||
        containerStatus.status_code === ContainerStatus.PUBLISHED
      ) {
        throw new Error('Error publishing to Instagram ' + containerStatus.status_code);
      }
      if (containerStatus.status_code === ContainerStatus.FINISHED) {
        const res = await publishSocialMediaContainer(
          { appKey, creationId, source: SocialMediaSources.instagram },
          publishMediaContainerSignal
        );
        if (res.id) {
          setIsPublishing(false);
          setSuccess(true);
          setError(null);
          return;
        }
      }
      if (containerStatus.status_code === ContainerStatus.IN_PROGRESS) {
        if (attempt > 3) {
          throw new Error('Error publishing to Instagram: too many attempts');
        }
        tryPublishTimeout.current = setTimeout(() => {
          tryPublish(creationId, attempt + 1);
        }, 5000);
      }
    } catch (error) {
      console.error(error);
      setIsPublishing(false);
      if ((error as Error).name !== 'AbortError') {
        setError({ title: errorTitle, body: errorMessage });
      }
    }
  };

  const handlePublish = async (event) => {
    // track('Publish to Instagram Modal Publish Button Click');
    setIsPublishing(true);
    setError(null);
    setSuccess(false);
    const mediaType = isVideo ? MediaType.reels : MediaType.image;
    const formData = new FormData(event.target);

    const caption = formData.get('caption')?.toString() || undefined;
    try {
      const limitUsage = await getLimitUsage({ appKey, source: SocialMediaSources.instagram }, getLimitUsageSignal);
      const usage = limitUsage.data?.[0]?.quota_usage || 0;
      const total = limitUsage.data?.[0]?.config?.quota_total || 50;
      const remaining = total - usage;
      if (remaining <= 0) {
        setError({
          title: 'Publish Limit Reached',
          body: 'You have reached the publish limit of your Instagram app. Please try again later.',
        });
        setIsPublishing(false);
        return;
      }

      const response = await createSocialMediaSingleItem(
        {
          appKey,
          assetUrl,
          mediaType,
          caption: caption || undefined,
          source: SocialMediaSources.instagram,
        },
        createSingleItemSignal
      );
      if (response.id) {
        tryPublishTimeout.current = setTimeout(
          () => {
            tryPublish(response.id, 0);
          },
          (duration + 10) * 1000
        );
      }
    } catch (error) {
      console.error(error);
      setIsPublishing(false);
      if ((error as Error).name !== 'AbortError') {
        setError({ title: errorTitle, body: errorMessage });
      }
    }
  };

  return (
    <>
      <Modal open={isOpen} onHide={handleClose}>
        <TitleBar title="Publish to Instagram" />
        <Box padding="400">
          <Layout>
            <Layout.Section>
              {success && (
                // TODO: use toast instead
                <Banner tone="success" title="Post Published!">
                  <Text as="p" variant="bodyMd">
                    Your media was published on your Instagram account.
                  </Text>
                </Banner>
              )}

              <DismissibleBanner
                tone="critical"
                sessionBanner={SessionBanner.PublishToInstagramError}
                showBanner={!!error}
                title={error?.title}
                action={error?.action}
              >
                <Text as="p" variant="bodyMd">
                  {error?.body}. Please try again or contact support.
                </Text>
              </DismissibleBanner>

              {isAppsLoading && <Spinner />}
              {openDiscardDialog && (
                <Banner
                  tone="warning"
                  title="Are you sure?"
                  action={{ content: 'Discard', onAction: handleDiscardPublish }}
                  onDismiss={() => setOpenDiscardDialog(false)}
                >
                  <Text as="p" variant="bodyMd">
                    This will discard the current publish.
                  </Text>
                </Banner>
              )}
            </Layout.Section>
            <Layout.Section>
              <InlineStack gap={{ xs: '200', sm: '300', md: '400', lg: '500', xl: '600' }}>
                <VideoCard video={video} hideActions />
                <div className="relative flex-grow">
                  <Form onSubmit={handlePublish}>
                    <FormLayout>
                      <TextField
                        autoFocus
                        name="caption"
                        label="Add a caption"
                        autoComplete="off"
                        value={caption}
                        onChange={setCaption}
                      />
                    </FormLayout>
                    <div className="absolute bottom-0 right-0 w-full">
                      <BlockStack gap="200" inlineAlign="end">
                        <ButtonGroup>
                          <Button onClick={handleClose}>Cancel</Button>
                          <Button submit variant="primary" disabled={isPublishing || isAppsLoading}>
                            Publish
                          </Button>
                        </ButtonGroup>
                        {isPublishing && <LoadingBar durationInSeconds={duration + 15} />}
                      </BlockStack>
                    </div>
                  </Form>
                </div>
              </InlineStack>
            </Layout.Section>
          </Layout>
        </Box>
      </Modal>
    </>
  );
}
